/** Connection snippets for coding agents (Claude Code, Codex, Cursor over MCP) and the CLI; shared by onboarding and the MCP guide */

import { API_BASE } from './api-client'

export const DEFAULT_MCP_URL = 'https://mcp.agentwiki.cc/mcp'
const DEFAULT_API_HOST = 'api.agentwiki.cc'
export const API_KEY_PLACEHOLDER = '<YOUR_API_KEY>'
export const CLI_PACKAGE = '@aiagentwiki/cli'

/** Absolute API origin the web app talks to, or null when it is same-origin / the hosted default */
export function customApiUrl(apiBase: string = API_BASE): string | null {
  try {
    const url = new URL(apiBase)
    return url.hostname === DEFAULT_API_HOST ? null : url.origin
  } catch {
    return null
  }
}

/** MCP endpoint: mcp.<domain> next to an api.<domain> deployment, else the hosted server */
export function mcpUrl(apiBase: string = API_BASE): string {
  const api = customApiUrl(apiBase)
  if (!api) return DEFAULT_MCP_URL
  const url = new URL(api)
  if (!url.hostname.startsWith('api.')) return DEFAULT_MCP_URL
  url.hostname = url.hostname.replace(/^api\./, 'mcp.')
  return `${url.origin}/mcp`
}

export type AgentConnectTab = 'claude-code' | 'codex' | 'cursor' | 'cli'
export const AGENT_CONNECT_TABS: readonly AgentConnectTab[] = ['claude-code', 'codex', 'cursor', 'cli']

/** JSON config for clients that read an `mcpServers` file (Cursor, Claude Desktop, .mcp.json) */
export function mcpJsonSnippet(apiKey: string = API_KEY_PLACEHOLDER, apiBase: string = API_BASE): string {
  return JSON.stringify({ mcpServers: { agentwiki: { url: mcpUrl(apiBase), headers: { Authorization: `Bearer ${apiKey}` } } } }, null, 2)
}

export interface ConnectSnippetInput {
  apiKey?: string
  project: string
  apiBase?: string
}

/** The copyable snippet for one connection tab */
export function connectSnippet(tab: AgentConnectTab, { apiKey = API_KEY_PLACEHOLDER, project, apiBase = API_BASE }: ConnectSnippetInput): string {
  const url = mcpUrl(apiBase)
  switch (tab) {
    case 'claude-code':
      return `claude mcp add --transport http agentwiki ${url} --header "Authorization: Bearer ${apiKey}"`
    case 'codex':
      return [
        `# ~/.codex/config.toml`,
        `[mcp_servers.agentwiki]`,
        `url = "${url}"`,
        `bearer_token_env_var = "AGENTWIKI_API_KEY"`,
        ``,
        `# in your shell profile`,
        `export AGENTWIKI_API_KEY=${apiKey}`,
      ].join('\n')
    case 'cursor':
      return mcpJsonSnippet(apiKey, apiBase)
    case 'cli': {
      const api = customApiUrl(apiBase)
      return [
        `npm install -g ${CLI_PACKAGE}`,
        `agentwiki login --api-key ${apiKey}${api ? ` --url ${api}` : ''}`,
        `agentwiki project use ${project}`,
        `agentwiki source push docs/ README.md --origin cli`,
        `agentwiki compile > brief.md`,
      ].join('\n')
    }
  }
}

/** Commands that compile the wiki in the user's own agent (free) */
export function agentCompileCommands(project: string): string {
  return [
    `agentwiki compile --project ${project} > brief.md`,
    `agentwiki wiki put <slug> --title "<title>" --topic "<section>" --file page.md --project ${project}`,
  ].join('\n')
}
